/**
 * OrganizersListDialog
 * Shows the full list of organizers in a ritual group chat, each linking
 * to the organizer's profile.
 */

import { ArrowLeft, HelpCircle, MoreVertical } from "lucide-react"
import { Link } from "react-router-dom"
import { Avatar } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { RitualGroupChatParticipant } from "../../types/ritual-group-chat.types"

interface OrganizersListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizers: RitualGroupChatParticipant[]
}

export function OrganizersListDialog({
  open,
  onOpenChange,
  organizers,
}: OrganizersListDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-full max-w-full md:h-fit md:max-h-[90vh] md:rounded-tr-2xl md:rounded-bl-2xl bg-[#020202] md:bg-white/6 border-0 md:border overflow-y-auto p-0 md:p-4"
        onInteractOutside={e => e.preventDefault()}
      >
        <div className="hidden md:block">
          <DialogHeader className="pt-5 px-3.5">
            <DialogTitle className="text-[24px] font-semibold uppercase">
              Organizers ({organizers.length})
            </DialogTitle>
            <DialogDescription className="text-[14px] text-muted-foreground leading-[25px]">
              View all organizers in this ritual group
            </DialogDescription>
          </DialogHeader>
        </div>

        <div>
          <div className="md:hidden sticky top-0 left-0 right-0 z-10 bg-[#020202] px-5 pt-3">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="p-2 -ml-2 cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="p-2 -mr-2 cursor-pointer">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  <DropdownMenuItem
                    onClick={() => window.open("mailto:support@myrituality.com", "_self")}
                  >
                    <HelpCircle className="w-4 h-4" />
                    Help Center
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto -mx-6 px-6 pt-4">
            {organizers.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">No organizers</p>
            ) : (
              organizers.map(organizer => {
                const displayName = organizer.name || organizer.username || "Unknown"
                return (
                  <Link
                    key={organizer.id}
                    to={`/profile/${organizer.id}`}
                    onClick={() => onOpenChange(false)}
                    className="w-full flex items-center gap-3 px-5 py-3.75 md:px-2 md:py-2.5 md:rounded-md hover:bg-accent transition-colors no-underline first:border-t border-b md:border-0 md:first:border-0 border-[#2A2A2A]"
                  >
                    <Avatar
                      src={organizer.profilePhoto || undefined}
                      alt={displayName}
                      fallback={displayName}
                      size="lg"
                    />
                    <div className="text-left min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                      {organizer.username && (
                        <p className="text-xs text-muted-foreground truncate">
                          @{organizer.username}
                        </p>
                      )}
                    </div>
                  </Link>
                )
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
